import React, { useState } from "react";
import {
	Row,
	Col,
	Alert,
	Card,
	CardBody,
	Container,
	FormFeedback,
	Input,
	Label,
	Form,
	InputGroup,
	InputGroupText,
} from "reactstrap";
import { Eye, EyeOff } from "lucide-react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import withRouter from "../../components/Common/withRouter";
import "../../assets/css/forgetpassword.css";
import { UpdatePassword } from "../../api/changePassword";
import { logoutUser } from "../../Context/authLogout";
import { Link } from "react-router-dom";
import FullScreenLoader from "../../components/Common/Loader";
import CommonToastContainer from "../../components/Common/CommonToastContainer";

const ChangePassword = (props) => {
	const dispatch = useDispatch();
	document.title = "Change Password";

	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [toasts, setToasts] = useState([]);

	const showToast = (toastData) => {
		setToasts((prev) => [...prev, { id: Date.now(), ...toastData }]);
	};

	const removeToast = (id) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			oldPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
		validationSchema: Yup.object({
			oldPassword: Yup.string().required("Please enter your old password"),
			newPassword: Yup.string()
				.required("Please enter a new password"),
			confirmPassword: Yup.string()
				.oneOf([Yup.ref("newPassword"), null], "Passwords must match")
				.required("Please confirm your new password"),
		}),
		onSubmit: async (values, { resetForm }) => {
			setShowLoader(true);

			try {
				const requestData = {
					CurrentPassword: values.oldPassword,
					NewPassword: values.newPassword,
					ConfirmNewPassword: values.confirmPassword,
				};
				const response = await UpdatePassword(requestData);
				if (response?.Message?.[0]?.RETURNCODE === "1") {
					showToast({
						title: "Success!",
						message: response.Message[0].RETURNMESSAGE || "Password changed successfully!",
						type: "success",
					});
					resetForm();
					setTimeout(() => {
						logoutUser();
					}, 2000);
				} else {
					showToast({
						title: "Error!",
						message: response?.Message?.[0]?.RETURNMESSAGE || "Failed to change password",
						type: "error",
					});
				}
			} catch (error) {
				console.error("Change Password API Error:", error);
				showToast({
					title: "Error!",
					message: "Something went wrong. Please try again.",
					type: "error",
				});
			} finally {
				setShowLoader(false);
			}
		},
	});

	return (
		<>
			<div
				className="bg-pattern d-flex flex-column"
				style={{
					backgroundImage: `linear-gradient(rgba(0,0,0,.20), rgba(0,0,0,.20)), url(/assets/images/backgroundImages/changePasswordBg.png)`,
					backgroundSize: "cover",
				}}
			>
				{showLoader && <FullScreenLoader />}
				<div className="account-pages flex-grow-1 main-background">
					<Container>
						<Row className="justify-content-center">
							<Col md={8} lg={6} xl={4}>
								<Card className="login-card p-3 mb-0" style={{
									borderRadius: "28px",
									overflow: "hidden",
									backgroundColor: "#ffffff"
								}}>
									<div className="login-banner">
										<img
											src="/assets/images/Logo/Midamas_Logo_2_15.png"
											alt="Midamas"
										/>
									</div>

									<CardBody className="p-0">
										<div className="">
											{/* Title */}
											<div className="text-center mt-4">
												<h4 className="fw-bold mb-2">Change Password</h4>
												<p className="text-muted mb-4">
													Please enter your old password and create a new one.
												</p>
											</div>

											{/* Form */}
											<Form
												className="form-horizontal"
												onSubmit={(e) => {
													e.preventDefault();
													validation.handleSubmit();
													return false;
												}}
											>
												{/* Old Password */}
												<div className="mb-3">
													<Label className="form-label">Old Password</Label>
													<InputGroup>
														<Input
															name="oldPassword"
															type={showOldPassword ? "text" : "password"}
															placeholder="Enter old password"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.oldPassword || ""}
															invalid={
																validation.touched.oldPassword &&
																	validation.errors.oldPassword
																	? true
																	: false
															}
														/>
														<InputGroupText
															onClick={() => setShowOldPassword(!showOldPassword)}
															style={{ cursor: "pointer" }}
														>
															{showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
														</InputGroupText>
														{validation.touched.oldPassword &&
															validation.errors.oldPassword && (
																<FormFeedback type="invalid">
																	{validation.errors.oldPassword}
																</FormFeedback>
															)}
													</InputGroup>
												</div>

												{/* New Password */}
												<div className="mb-3">
													<Label className="form-label">New Password</Label>
													<InputGroup>
														<Input
															name="newPassword"
															type={showNewPassword ? "text" : "password"}
															placeholder="Enter new password"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.newPassword || ""}
															invalid={
																validation.touched.newPassword &&
																	validation.errors.newPassword
																	? true
																	: false
															}
														/>
														<InputGroupText
															onClick={() => setShowNewPassword(!showNewPassword)}
															style={{ cursor: "pointer" }}
														>
															{showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
														</InputGroupText>
														{validation.touched.newPassword &&
															validation.errors.newPassword && (
																<FormFeedback type="invalid">
																	{validation.errors.newPassword}
																</FormFeedback>
															)}
													</InputGroup>
												</div>

												{/* Confirm New Password */}
												<div className="mb-3">
													<Label className="form-label">Confirm New Password</Label>
													<InputGroup>
														<Input
															name="confirmPassword"
															type={showConfirmPassword ? "text" : "password"}
															placeholder="Confirm new password"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.confirmPassword || ""}
															invalid={
																validation.touched.confirmPassword &&
																	validation.errors.confirmPassword
																	? true
																	: false
															}
														/>
														<InputGroupText
															onClick={() => setShowConfirmPassword(!showConfirmPassword)}
															style={{ cursor: "pointer" }}
														>
															{showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
														</InputGroupText>
														{validation.touched.confirmPassword &&
															validation.errors.confirmPassword && (
																<FormFeedback type="invalid">
																	{validation.errors.confirmPassword}
																</FormFeedback>
															)}
													</InputGroup>
												</div>

												{/* Submit Button */}
												<div className="d-grid mt-4">
													<button
														className="btn btn-primary waves-effect waves-light"
														type="submit"
														disabled={loading}
													>
														{loading ? "Updating..." : "Save Changes"}
													</button>
												</div>
											</Form>
										</div>

										<div className="text-center mt-3">
											<Link to="/user-profile" className="text-primary fw-semibold">
												Back to your profile
											</Link>
										</div>
									</CardBody>
								</Card>
							</Col>
						</Row>
					</Container>
				</div>
			</div>
			<CommonToastContainer toasts={toasts} removeToast={removeToast} />
		</>
	);
};

export default withRouter(ChangePassword);
